/**
 * @file notification.service.ts
 * @description App-level service for the notifications bell menu and the
 * share-approval workflow. Wraps the Registry API `/notifications/*`
 * endpoints with typed request/response DTOs from
 * `@kirigami/data-access/registry-api-contracts`.
 *
 * What belongs here (today):
 * - list + mark-read for the header bell
 * - approve / reject share invitations received by the current user
 * - create a new notification (used by the share dialog to leave an audit
 *   trail + bell entry on the recipient side)
 *
 * Factor out to a shared `notification-adapters-web` lib if word-addin or
 * another app ever needs the same surface.
 *
 * @see legacy/kirigami-analysis/src/app/shared/services/notification.service.ts
 */
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import type {
  CreateNotificationRequest,
  KirigamiNotificationDto,
} from '@kirigami/data-access/registry-api-contracts';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  readonly #http = inject(HttpClient);

  #url(path: string): string {
    return `${environment.apiBaseUrl}${path}`;
  }

  /** `GET /v1/notifications/` — list notifications for the current user. */
  fetchNotifications(): Observable<KirigamiNotificationDto[]> {
    return this.#http.get<KirigamiNotificationDto[]>(this.#url('/notifications/')).pipe(
      catchError((error) => {
        console.error('[NotificationService] Failed to fetch notifications:', error);
        return throwError(() => new Error('Failed to load notifications.'));
      }),
    );
  }

  /**
   * `POST /v1/notifications/` — create a notification for the recipient.
   * The share dialog fires this alongside `addUserToReport` so the
   * recipient gets a bell entry and an audit trail for the invitation.
   */
  createNotification(payload: CreateNotificationRequest): Observable<KirigamiNotificationDto> {
    return this.#http.post<KirigamiNotificationDto>(this.#url('/notifications/'), payload).pipe(
      catchError((error) => {
        console.error('[NotificationService] Failed to create notification:', error);
        const detail =
          (error?.error && typeof error.error === 'object' && error.error.detail) || error?.message;
        return throwError(() => new Error(detail || 'Failed to create notification.'));
      }),
    );
  }

  /** `PUT /v1/notifications/approve-report-share` — recipient accepts a share invite. */
  approveShare(notificationId: string): Observable<void> {
    return this.#http
      .put<void>(this.#url('/notifications/approve-report-share'), {
        id: notificationId,
        action_status: 'APPROVED',
        type: 'REPORT_SHARE_APPROVAL',
      })
      .pipe(
        catchError((error) => {
          console.error('[NotificationService] Failed to approve share:', error);
          return throwError(() => new Error('Failed to approve share request.'));
        }),
      );
  }

  /** `PUT /v1/notifications/reject-report-share` — recipient declines a share invite. */
  rejectShare(notificationId: string): Observable<void> {
    return this.#http
      .put<void>(this.#url('/notifications/reject-report-share'), {
        id: notificationId,
        action_status: 'REJECTED',
        type: 'REPORT_SHARE_APPROVAL',
      })
      .pipe(
        catchError((error) => {
          console.error('[NotificationService] Failed to reject share:', error);
          return throwError(() => new Error('Failed to reject share request.'));
        }),
      );
  }

  /** `PUT /v1/notifications/update-status` — mark a notification as read. */
  markAsRead(notificationId: string, type: string): Observable<void> {
    return this.#http
      .put<void>(this.#url('/notifications/update-status'), {
        id: notificationId,
        status: 'READ',
        type,
      })
      .pipe(
        catchError((error) => {
          console.error('[NotificationService] Failed to mark as read:', error);
          return throwError(() => new Error('Failed to update notification.'));
        }),
      );
  }
}
